import {
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export type VideoProvider = 'youtube' | 'vimeo';

@Component({
  selector: 'ds-video-embed-block',
  standalone: true,
  templateUrl: './video-embed-block.html',
  styleUrl: './video-embed-block.scss',
  encapsulation: ViewEncapsulation.None,
})
export class VideoEmbedBlockComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly doc = inject(DOCUMENT);

  constructor() {
    effect(() => {
      const json = this.jsonLd();
      const root = this.host.nativeElement as HTMLElement;
      const existing = root.querySelector('script[type="application/ld+json"][data-video-jsonld]');
      if (existing) existing.remove();
      if (json) {
        const script = this.doc.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-video-jsonld', '');
        script.textContent = json;
        root.appendChild(script);
      }
    });
  }

  readonly provider = input<VideoProvider>('youtube');
  readonly videoId = input.required<string>();
  readonly title = input<string>('');
  readonly caption = input<string>('');
  readonly fullWidth = input<boolean>(false);
  readonly posterUrl = input<string | null>(null);
  readonly uploadDate = input<string>('');

  readonly playing = signal(false);

  readonly ariaLabel = computed(() => 'Play video: ' + this.title());

  readonly thumbnailSrcMax = computed<string | null>(() => {
    const override = this.posterUrl();
    if (override) return override;
    if (this.provider() === 'youtube') {
      return `https://i.ytimg.com/vi/${this.videoId()}/maxresdefault.jpg`;
    }
    return null;
  });

  readonly thumbnailFallback = computed<string | null>(() => {
    const override = this.posterUrl();
    if (override) return override;
    if (this.provider() === 'youtube') {
      return `https://i.ytimg.com/vi/${this.videoId()}/hqdefault.jpg`;
    }
    return null;
  });

  private rawEmbedUrl(withAutoplay: boolean): string {
    const id = this.videoId();
    if (this.provider() === 'vimeo') {
      return withAutoplay
        ? `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`
        : `https://player.vimeo.com/video/${id}?dnt=1`;
    }
    return withAutoplay
      ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`
      : `https://www.youtube-nocookie.com/embed/${id}?rel=0`;
  }

  readonly iframeSrc = computed<SafeResourceUrl>(() =>
    this.sanitizer.bypassSecurityTrustResourceUrl(this.rawEmbedUrl(true)),
  );

  readonly jsonLd = computed<string | null>(() => {
    if (!this.title() || !this.caption()) return null;
    const thumb =
      this.thumbnailSrcMax() ?? this.thumbnailFallback() ?? '';
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: this.title(),
      description: this.caption(),
      thumbnailUrl: thumb,
      uploadDate: this.uploadDate() || new Date().toISOString(),
      embedUrl: this.rawEmbedUrl(false),
    });
  });

  activate(): void {
    this.playing.set(true);
  }

  onKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
      ev.preventDefault();
      this.activate();
    }
  }
}
